/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback } from 'react';
import type { ComponentProps, MouseEvent, ReactElement } from 'react';
import { css } from '@compiled/react';

import { isSafeUrl } from '@atlaskit/adf-schema/is-safe-url';
import { cssMap, jsx } from '@atlaskit/css';
import LinkIcon from '@atlaskit/icon/core/link';
import { useSmartCardContext } from '@atlaskit/link-provider/use-smart-card-context';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import type { InlineCard as OriginalInlineCard } from '@atlaskit/renderer/nodes';
import { token } from '@atlaskit/tokens';

import type { LightNodeProps } from '../LightNodeObserver.ts';

// Match Platform InlineCard/Frame and IconAndTitleLayout without mounting card providers.
const styles = cssMap({
	frame: {
		display: 'inline',
		boxDecorationBreak: 'clone',
		borderRadius: token('radius.medium'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('elevation.surface.raised'),
		paddingBlockStart: token('space.025'),
		paddingBlockEnd: token('space.025'),
		textDecoration: 'none',
	},
	content: {
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-all',
		boxDecorationBreak: 'clone',
		paddingBlockStart: token('space.025'),
		paddingBlockEnd: token('space.025'),
		paddingInlineStart: token('space.050'),
		paddingInlineEnd: token('space.050'),
	},
	iconPosition: {
		display: 'inline-block',
		position: 'relative',
		marginInlineEnd: token('space.050'),
	},
	iconSpace: {
		display: 'inline-block',
		width: '16px',
		height: '100%',
	},
	icon: {
		display: 'inline-flex',
		position: 'absolute',
		width: '16px',
		height: '16px',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
	},
});

// @atlaskit/css disallows lineHeight; Platform's icon baseline requires this relative value.
// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Matches Platform IconAndTitleLayout's relative icon baseline.
const iconBaseline = css({ lineHeight: '1' });

/** A readable link without card resolution, subscriptions, previews or drag handlers. */
export const InlineCard = ({
	url,
	eventHandlers,
	onSetLinkTarget,
	lightNodeRef,
}: ComponentProps<typeof OriginalInlineCard> & LightNodeProps): ReactElement => {
	const { value: context } = useSmartCardContext();
	const card = url ? context?.store.getState()[url] : undefined;
	// Do not display cached metadata for forbidden, unauthorized or unresolved cards.
	const data = card?.status === 'resolved' ? card.details?.data : undefined;
	const cachedTitle =
		data && 'name' in data && typeof data.name === 'string' ? data.name : undefined;
	const title = cachedTitle?.trim() ? cachedTitle : url;
	// Match Platform getClickUrl: metadata.url is not the navigation destination.
	const visitUrl = data?.['atlassian:visitUrl'];
	const resolvedUrl = visitUrl || url;
	const navigation = resolvedUrl ? context?.linkNavigation?.(resolvedUrl) : undefined;
	const destination = navigation?.url ?? resolvedUrl;
	const handleClick = useCallback(
		(event: MouseEvent<HTMLAnchorElement>) => {
			const onClick = eventHandlers?.smartCard?.onClick;
			if (onClick && destination) {
				event.preventDefault();
				event.stopPropagation();
				onClick(event, destination);
			}
		},
		[destination, eventHandlers?.smartCard?.onClick],
	);

	if (!destination || !isSafeUrl(destination)) {
		return <span ref={lightNodeRef}>{title}</span>;
	}

	return (
		<Anchor
			ref={lightNodeRef}
			xcss={styles.frame}
			data-inline-card
			data-card-url={url}
			href={destination}
			target={navigation?.target ?? onSetLinkTarget?.(destination)}
			rel="noopener noreferrer"
			onClick={handleClick}
		>
			<span css={styles.content}>
				<span css={[styles.iconPosition, iconBaseline]} aria-hidden="true">
					<span css={styles.iconSpace} />
					<span css={styles.icon}>
						<LinkIcon label="" color="currentColor" />
					</span>
				</span>
				<span>{title}</span>
			</span>
		</Anchor>
	);
};
